import { computed, inject, Injectable, Type } from '@angular/core';
import { SidePanelSize } from '@app/+authenticated/shared/app-side-panel/app-side-panel.component';
import { ChecklistPanelComponent } from '@app/+side-panel/checklist-panel/checklist-panel.component';
import { RequestsPanelComponent } from '@app/+side-panel/requests-panel/requests-panel.component';
import { SupportPanelComponent } from '@app/+side-panel/support-panel/support-panel.component';

import {
  isComponentMapKey,
  SidePanelStateService,
  type ComponentMapKey,
  type SidePanelInputs,
} from './side-panel-state.service';

export type { ComponentMapKey } from './side-panel-state.service';
export { isComponentMapKey };

interface SidePanelDefinition {
  component: Type<unknown>;
  // Omit to keep the container's default ('normal') size.
  size?: SidePanelSize;
}

const COMPONENT_MAP: Record<ComponentMapKey, SidePanelDefinition> = {
  support: { component: SupportPanelComponent },
  checklist: { component: ChecklistPanelComponent },
  requests: { component: RequestsPanelComponent, size: 'wide' },
};

@Injectable({
  providedIn: 'root',
})
export class SidePanelService {
  #state = inject(SidePanelStateService);

  activeComponent = this.#state.activeComponent;
  activeComponentInputs = this.#state.activeInputs;

  activeSize = computed<SidePanelSize>(() => {
    const activeKey = this.#state.activeKey();

    return (activeKey ? COMPONENT_MAP[activeKey].size : undefined) ?? 'normal';
  });

  setActiveComponent(path: ComponentMapKey | null, inputs: SidePanelInputs = {}) {
    if (!path) {
      return this.#state.close();
    }

    this.#state.setActive(path, COMPONENT_MAP[path].component, inputs);
  }

  close() {
    this.#state.close();
  }

  isActive(path: ComponentMapKey) {
    return this.#state.isActive(path);
  }
}
